import { useState, useMemo, useEffect } from 'react';

import { SelectedVariantSummary } from './SelectedVariantSummary';
import { ProductVariantListItem } from './ProductVariantListItem';
import { ProductsV2Autocomplete } from './ProductsV2Autocomplete';
import { Label } from '@radix-ui/react-label';
import { Grid } from '@/components/common/grid';
import { ProductAttributeSelection } from './ProductAttributeSelection';
import { Button } from '../ui/button';
import { useProductVariants } from '@/api/inventory-management/products/useProductVariants';
import { useIsOneToOneProductMapping } from '@/hooks/useIsOneToOneProductMapping';
import { SpinnerIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';

type ProductVariantMatch = {
  productVariantId: string;
  confidence: number;
};

export function AssociateProductV2({
  allowProductSelection,
  selectedProductVariantId,
  selectedProductId,
  topProductVariantMatches,
  onVariantSelected,
  onProductSelected,
  children,
  customerId,
}: {
  allowProductSelection?: boolean;
  selectedProductVariantId?: string;
  selectedProductId?: string;
  topProductVariantMatches?: ProductVariantMatch[];
  editable?: boolean;
  onVariantSelected: ({
    productId,
    productVariantId,
  }: {
    productId?: string;
    productVariantId?: string;
  }) => void;
  onProductSelected?: (productId: string | undefined) => void;
  children?: React.ReactNode;
  customerId?: string;
}) {
  const isOneToOneProduct = useIsOneToOneProductMapping();

  const [selectedProductIdForVariant, setSelectedProductIdForVariant] =
    useState<string | undefined>(selectedProductId);

  const { data: productVariantsData, isLoading: isLoadingVariants } =
    useProductVariants(
      selectedProductIdForVariant ?? '',
      isOneToOneProduct && !!selectedProductIdForVariant,
    );

  // Auto-select the sole variant when variants are disabled
  useEffect(() => {
    if (!isOneToOneProduct) return;
    if (!selectedProductIdForVariant) return;
    if (!productVariantsData?.variants?.length) return;

    const soleVariant = productVariantsData.variants[0];
    if (soleVariant.id === selectedProductVariantId) return;

    onVariantSelected({
      productVariantId: soleVariant.id,
      productId: selectedProductIdForVariant,
    });
  }, [
    isOneToOneProduct,
    selectedProductIdForVariant,
    productVariantsData?.variants,
    selectedProductVariantId,
    onVariantSelected,
  ]);

  const topVariantIds = useMemo(() => {
    if (!topProductVariantMatches) return [];
    return topProductVariantMatches
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 5)
      .map((match) => match.productVariantId);
  }, [topProductVariantMatches]);

  const handleVariantSelect = (productVariantId: string) => {
    onVariantSelected({
      productVariantId,
      productId: selectedProductIdForVariant,
    });
  };

  const renderTopMatches = () => {
    if (topVariantIds.length === 0) return null;

    return (
      // Full-width item: a plain div would land in a single 1-of-12 column
      // track of the surrounding Grid container and get squeezed.
      <Grid item xs={12}>
        <h2 className="text-base font-medium">
          <T context="Heading for suggested product variant matches">
            Top Matches
          </T>
        </h2>
        <ul className="list-none space-y-1">
          {topVariantIds.map((variantId) => {
            return (
              <ProductVariantListItem
                key={variantId}
                variantId={variantId}
                isSelected={selectedProductVariantId === variantId}
                onSelect={handleVariantSelect}
              />
            );
          })}
        </ul>
      </Grid>
    );
  };

  const renderNoVariantsStatus = () => {
    if (!selectedProductIdForVariant) return null;

    if (isLoadingVariants) {
      return (
        <Grid item xs={12}>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <SpinnerIcon className="size-4 animate-spin" />
            <T context="Loading while resolving product variant selection">
              Selecting variant...
            </T>
          </div>
        </Grid>
      );
    }

    if (
      productVariantsData?.variants &&
      productVariantsData.variants.length === 0
    ) {
      return (
        <Grid item xs={12}>
          <div className="text-sm text-destructive">
            <T context="Error when product has no variants to select">
              No variant found for this product.
            </T>
          </div>
        </Grid>
      );
    }

    return null;
  };

  return (
    <Grid container spacing={2}>
      {/* Full-width item: plain children would land in a single 1-of-12
          column track of the Grid container and get squeezed. */}
      {children ? (
        <Grid item xs={12}>
          {children}
        </Grid>
      ) : null}

      {selectedProductVariantId && (
        <Grid item xs={12}>
          <SelectedVariantSummary variantId={selectedProductVariantId} />
        </Grid>
      )}

      {renderTopMatches()}

      {/* Product Search */}
      <Grid item xs={12} className="flex gap-2">
        <Label className="flex flex-col gap-2 grow">
          {topVariantIds.length === 0 ? (
            <T context="Label for product search when no top matches">
              Match to a product
            </T>
          ) : (
            <T context="Label for product search when alternatives exist">
              Or Select Another Product
            </T>
          )}
          <ProductsV2Autocomplete
            selectedProductId={selectedProductIdForVariant}
            onProductSelect={(productId) => {
              setSelectedProductIdForVariant(productId);
            }}
          />
        </Label>
        {allowProductSelection && (
          <Button
            onClick={() => onProductSelected?.(selectedProductIdForVariant)}
            className="self-end shrink"
          >
            <T context="Button to confirm product without variant">
              Select Product Only
            </T>
          </Button>
        )}
      </Grid>

      {isOneToOneProduct
        ? renderNoVariantsStatus()
        : selectedProductIdForVariant && (
            <ProductAttributeSelection
              selectedProductId={selectedProductIdForVariant}
              selectedVariantId={selectedProductVariantId}
              onSelectVariant={handleVariantSelect}
              customerId={customerId}
            />
          )}
    </Grid>
  );
}

export default AssociateProductV2;
